import { AdditionalServicesService } from '@additional-services/services/additional-services.service';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CompleteOrderService } from '@complete-order/services/complete-order.service';
import {
  CurrentPlan,
  ItemOffer,
  Offer,
  PlanType,
  ReplacementOffer,
  UpsellExclusions,
  fadeInOut,
  slideOut,
} from '@dgx-pr-spa/shared/util';
import { ProductDetailsService } from '@product-details/services/product-details.service';
import { isUpsellExclusion, planType } from '@shared/helpers/function.helpers';
import { ViewOffersService } from '@view-offers/services/view-offers.service';
import {
  AddProtectionHelper,
  UpsellContent,
} from './add-protection-container.helper';
import { filter, switchMap, take } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ActivatedRoute, Router } from '@angular/router';

@UntilDestroy()
@Component({
  selector: 'app-add-protection-container',
  templateUrl: './add-protection-container.component.html',
  styleUrls: ['./add-protection-container.component.scss'],
  animations: [slideOut, fadeInOut],
  providers: [AddProtectionHelper],
})
export class AddProtectionContainerComponent implements OnChanges, OnInit {
  constructor(
    public readonly viewOffersService: ViewOffersService,
    public readonly completeOrderService: CompleteOrderService,
    public readonly productDetailsService: ProductDetailsService,
    public readonly additionalServicesService: AdditionalServicesService,
    public readonly addProtectionHelper: AddProtectionHelper,
    private readonly router: Router,
    private readonly activateRoute: ActivatedRoute
  ) {}

  /**
   * Emits a checked event of T&Cs back to the parent component
   */
  @Output() checked: EventEmitter<boolean> = new EventEmitter();

  /**
   * Emits an event when `i want this one` is clicked to the parent component
   */
  @Output() confirmSelected: EventEmitter<void> = new EventEmitter();

  /**
   * Emits toggle of selected extra
   */
  @Output() selected: EventEmitter<boolean> = new EventEmitter();

  /**
   * Emits policy protect open or closed
   */
  @Output() toggledPolicyBenefits: EventEmitter<string> = new EventEmitter();

  offers$ = this.viewOffersService.offers$;
  selected$ = this.productDetailsService.selected$;
  offerReference$ = this.viewOffersService.offerReference$;

  /**
   * Receives the itemOffer reference
   */
  itemOffer$ = this.additionalServicesService.itemOffer$;

  /**
   * Receives the selected offer
   */
  @Input() selectedOffer: ReplacementOffer;

  /**
   * Receives the current offer
   */
  @Input() offer: Offer;

  /**
   * Receives item offer data
   */
  @Input() itemOffer: ItemOffer;

  /**
   * Check offer upsell toggle
   */
  @Input() upsellEnabled = true;

  /**
   * Receives item offer error
   */
  @Input() itemOfferError;

  /**
   * Receives current plan
   */
  @Input() currentPlan: CurrentPlan;

  /**
   * Receives boolean of protection selected state
   */
  @Input() isProtectionSelected: boolean;

  /**
   * Receives boolean of decline protection selected state
   */
  @Input() isDeclineProtectionSelected: boolean;

  /**
   * Receives boolean of delivery available
   */
  @Input() isDeliveryAvailable: boolean;

  /**
   * Receives boolean of terms & conditions selected state
   */
  @Input() isTermsConditionsSelected: boolean;

  /**
   * Receives the current offer
   */
  @Input() optimizely: boolean;

  /**
   * Tracks policy accordion state
   */
  isUpsellIncludedOpen = false;

  /**
   * Tracks policy advantages accordion state
   */
  isUpsellAdvantagesOpen = false;

  /**
   * Text that gets displayed when upsell_flag is false
   */
  upsellMessage: string = null;

  /**
   * Boolean to determine whether to show policy comparison
   */
  showPolicyComparison = false;

  /**
   * Business rule exceptions
   */
  UpsellExclusions = UpsellExclusions;

  /**
   * Upsell Inclusions + Exclusions copy
   */
  upsellContent: UpsellContent;

  ngOnInit() {
    this.additionalServicesService.itemOfferLoaded$
      .pipe(
        untilDestroyed(this),
        filter((loaded) => loaded),
        switchMap(() =>
          this.additionalServicesService.itemOfferError$.pipe(
            untilDestroyed(this),
            filter((errored) => errored),
            take(1)
          )
        )
      )
      .subscribe(() => {
        this.router.navigate(['/complete-order'], {
          queryParams: { PRRef: this.activateRoute.snapshot.queryParams.PRRef },
          replaceUrl: true,
        });
      });
  }

  /**
   * Reset the accordion open state when new offer is selected
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes?.itemOffer) {
      this.isUpsellIncludedOpen = false;
      this.showPolicyComparison = this.optimizely;
    }

    // upsell_flag is TRUE but /item/offer doesn't return a new policy rate due to error, no scheme mapping etc.
    if (changes?.itemOfferError && changes.itemOfferError.currentValue) {
      this.setUpsellMessage('error');
    } else if (
      changes?.offer &&
      !changes.offer.currentValue?.upsell_flag &&
      changes.offer.currentValue?.upsell_reason
    ) {
      this.setUpsellMessage(changes.offer.currentValue.upsell_reason);
    } else {
      // no message will be shown
      this.upsellMessage = this.upsellMessage || null;
    }

    this.upsellContent =
      this.addProtectionHelper.upsellInclusionsExclusionsCopy(
        this.currentPlan?.scheme_code
      );
  }

  /**
   * Depending on values of upsell_flag and upsell_reason define a message that should be displayed instead of the upsell box
   * @param reason - an optional text string returned from /offers endpoint (upsell_reason)
   * if no parameter is passed in the message is set to dafault value
   */
  setUpsellMessage(reason?: string) {
    if (reason === 'error') {
      this.upsellMessage =
        'We’re currently unable to display the policy price at the moment, so we’ll contact you with the information instead.';
    } else {
      this.upsellMessage = `Your new model will be protected as part of your existing ${
        isUpsellExclusion(reason) ? UpsellExclusions[reason] : ''
      } plan.`;
    }
  }

  /**
   * Click handler for selecting protection
   */
  onSelectProtection(checked: boolean) {
    this.isUpsellIncludedOpen = checked;
    this.selected.emit(checked);
    if (checked) {
      if (this.isDeclineProtectionSelected) {
        this.onDeclineProtection(false);
      }
      this.additionalServicesService.addProtection();
    } else {
      this.additionalServicesService.resetProtection();
      this.additionalServicesService.resetTermsConditions();
    }
  }

  /**
   * Click handler for declining protection
   */
  onDeclineProtection(checked: boolean) {
    if (checked) {
      this.isUpsellIncludedOpen = false;
      this.selected.emit(false);
    }

    if (checked) {
      this.onSelectProtection(false);
      this.additionalServicesService.declineProtection();
    } else {
      this.additionalServicesService.resetDeclineProtection();
    }
  }

  /**
   * Click handler for terms & conditions
   */
  onTermsChecked(checked: boolean) {
    this.checked.emit(checked);
    if (checked) {
      this.additionalServicesService.agreeTermsConditions();
    } else {
      this.additionalServicesService.resetTermsConditions();
    }
  }

  /**
   * Click handler for clicking "I want this one"
   */
  confirmSelectClick() {
    this.confirmSelected.emit();
  }

  /**
   * Track state of terms and conditions whether they have not been checked
   */
  isTermsRequired() {
    return !!(this.isProtectionSelected && !this.isTermsConditionsSelected);
  }

  /**
   * Track state of upsell and if the user can continue without checking a checkbox
   */
  isUpsellInvalid() {
    return (
      !this.isProtectionSelected &&
      !this.isDeclineProtectionSelected &&
      this.offer?.upsell_flag
    );
  }

  /**
   * Track toggle policy beneifts
   */
  togglePolicyBenefits(event: string) {
    this.toggledPolicyBenefits.emit(event);
  }

  isSinglePlanUpsellDisabled() {
    return (
      planType(this.currentPlan) === PlanType.SINGLE && !this.offer?.upsell_flag
    );
  }
}
